import React, { FocusEvent, Key, useCallback, useRef } from 'react';
import { KeyboardEvent } from '@react-types/shared';
import { isMobile } from '@direct-frontend/platform-code';

import { Combobox } from '../../../Combobox/Combobox';
import { TagTextInput } from '../TagTextInput/TagTextInput';
import { ListBoxItem, ListBoxOptionItem } from '../../../ListBox';
import { isSection } from '../../../ListBoxBase/Item/utils';
import { InputRenderer } from '../../../Combobox/types';
import { getSizeSkin } from '../../../../lib/skin/getSizeSkin/getSizeSkin';
import { cn } from '../../../../lib/css/className/className';

import { TagComboboxEditor as TagComboboxEditorMobile } from './TagComboboxEditor@mobile';
import { TagComboboxEditorProps } from './types';

import './TagComboboxEditor.css';

const cls = cn('TagComboboxEditor');

const defaultInputRenderer: InputRenderer = props => <TagTextInput {...props} />;

const flattenItems = <ValueType extends string>(items: ListBoxItem<ValueType>[]): ListBoxOptionItem<ValueType>[] => {
    return items.reduce<ListBoxOptionItem<ValueType>[]>((result, item) => {
        if (isSection(item)) {
            result.push(...item.options);
        } else {
            result.push(item);
        }

        return result;
    }, []);
};

export const TagComboboxEditor = function TagComboboxEditor<ValueType extends string, DataT = unknown>(
    props: TagComboboxEditorProps<ValueType, DataT>,
) {
    const {
        items,
        onAdd,
        inputValue,
        onChange,
        onKeyDown,
        onCustomValueAdd,
        onCustomValueEdit,
        clearTagIndex,
        flip = 'once',
        selectedTagIndex,
        autoFocus = true,
        virtualized = false,
        children = defaultInputRenderer,
        tagGroupSize = 'm',
        isAddOnBlur = true,
        preserveInputOnAdd = false,
    } = props;
    // Флаг нужен, чтобы понимать, что пользователь выбрал какой-то элемент из списка и не вызывать onCustomValueAdd
    const itemSelected = useRef(false);
    const isNewTabRef = useRef(false);

    const onSelectionChange = useCallback(
        (key: Key | null) => {
            const item = flattenItems(items).find(item => item.value === key);
            if (item) {
                onAdd?.(item);
                if (!preserveInputOnAdd) {
                    onChange('');
                }
                itemSelected.current = true;
            }
        },
        [items, onAdd, onChange, preserveInputOnAdd],
    );

    const onAddHandler = useCallback(() => {
        const trimmedValue = inputValue?.trim();

        if (
            (onCustomValueAdd || onCustomValueEdit) &&
            !itemSelected.current &&
            trimmedValue &&
            trimmedValue.length !== 0
        ) {
            if (selectedTagIndex === -1 || selectedTagIndex === undefined) {
                onCustomValueAdd?.(trimmedValue);
            } else {
                onCustomValueEdit?.(trimmedValue, selectedTagIndex);
                clearTagIndex?.();
            }

            onChange('');

            return true;
        }

        return false;
    }, [inputValue, selectedTagIndex, clearTagIndex, onChange, onCustomValueAdd, onCustomValueEdit]);

    const keyDownHandler = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                onAddHandler();
            }

            itemSelected.current = false;
            onKeyDown?.(event);
        },
        [onKeyDown, onAddHandler],
    );
    const blurHandler = useCallback(
        (event?: FocusEvent) => {
            // Так как blurHandler срабатывает вместе с методом visibilitychange,
            // то нам необходима обертка в виде setTimeout
            // Она гарантирует, что метод visibilitychange успеет сработать
            // И нам придет обновленное состояние isNewTabRef
            // setTimeout переводит выполнение логики внутри на следующий event loop
            setTimeout(() => {
                if (isNewTabRef.current) {
                    return;
                }

                const inputCleared = isAddOnBlur && onAddHandler();

                if (preserveInputOnAdd && !inputCleared) {
                    onChange('');
                }

                itemSelected.current = false;
                props.onBlur?.(event);
            }, 0);
        },
        [onAddHandler, isAddOnBlur, props, preserveInputOnAdd, onChange],
    );

    // Здесь происходит проверка при переключении между вкладками
    // Она нужна для того, чтобы избежать ререндера выпадающего меню при переходе по вкладкам
    React.useEffect(() => {
        const visibilityChange = () => {
            // Тут следим за изменением по переходу на другие вкладки
            // Если перешли на другую вкалдку то isNewTabRef.current === true
            isNewTabRef.current = document.visibilityState !== 'visible';
        };

        document.addEventListener('visibilitychange', visibilityChange, true);

        return () => document.removeEventListener('visibilitychange', visibilityChange, true);
    }, []);

    const pseudoTriggerSizeSkinClass = getSizeSkin(tagGroupSize);
    const pseudoTriggerRef = useRef<HTMLDivElement>(null);

    if (isMobile()) {
        return (
            <TagComboboxEditorMobile
                {...props}
                onSelectionChange={onSelectionChange}
                onKeyDown={keyDownHandler}
                onBlur={blurHandler}
            />
        );
    }

    return (
        <>
            {/**
             * div, отображающийся над combobox, на всю ширину TagGroup
             *
             * Нужен для позиционирования Popup,
             * чтобы Popup мог отобразиться с направлением вверх и поверх выбранных тэгов
             */}
            <div ref={pseudoTriggerRef} className={cls('pseudoTrigger', [pseudoTriggerSizeSkinClass])} />
            <Combobox
                {...props}
                onBlur={blurHandler}
                onSelectionChange={onSelectionChange}
                onKeyDown={keyDownHandler}
                selectedKey={null}
                inputValue={inputValue}
                onInputChange={onChange}
                allowsCustomValue={Boolean(onCustomValueAdd)}
                flip={flip}
                autoFocus={autoFocus}
                virtualized={virtualized}
                size={tagGroupSize}
                // Перезаписываем прокинутый из TagGroup ref
                containerRef={pseudoTriggerRef}
            >
                {props =>
                    children({
                        ...props,
                        size: tagGroupSize,
                    })
                }
            </Combobox>
        </>
    );
};
